import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Clock,
  Download,
  Eye,
  Loader2,
  MapPin,
  RotateCcw,
  Search,
  Truck,
} from "lucide-react";

import useOrders from "../../Services/useOrders";
import { useGlobalContext } from "../../Context/GlobalContext";
import NoCafe from "../../Images/Restaurant_Logo_Placeholder.webp";
import OrderDetailsModal from "./OrderDetailsModal";
import { useLocation, useNavigate } from "react-router-dom";
import useDriverTracker from "../../Services/usedriverTracker";

const filters = ["All", "Preparing", "Delivered", "Cancelled", "Refunded"];

const formatAmount = (amount) => {
  const num = Number(amount);

  if (Number.isNaN(num)) {
    return "0.00";
  }

  return num.toFixed(2);
};

const formatOrderDate = (value) => {
  if (!value) return "Date unavailable";

  const timestamp = parseOrderDate(value);

  if (!timestamp) return String(value);

  return new Intl.DateTimeFormat("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  }).format(new Date(timestamp));
};

const formatCustomerMobile = (value) => {
  const digits = String(value || "").replace(/\D/g, "");

  if (!digits) {
    return "";
  }

  if (digits.startsWith("91") && digits.length === 12) {
    return digits;
  }

  if (digits.length === 10) {
    return `91${digits}`;
  }

  return digits;
};

const normalizeValue = (value) => {
  return String(value || "")
    .trim()
    .toLowerCase()
    .replaceAll("_", " ");
};


const getOrderImage = (order) => {
  const image = String(
    order?.image ||
      order?.restaurant_image ||
      order?.restaurantImage ||
      order?.restaurant_logo ||
      order?.restaurantLogo ||
      order?.organization_logo ||
      order?.organizationLogo ||
      order?.brand_image ||
      order?.brandImage ||
      "",
  ).trim();

  return image || NoCafe;
};
const isDeliveredOrder = (order) => {
  const status = normalizeValue(order?.status);
  const deliveryStatus = normalizeValue(order?.delivery_partner_status);

  return (
    status.includes("completed") ||
    status.includes("delivered") ||
    deliveryStatus.includes("successful") ||
    deliveryStatus.includes("delivered")
  );
};

const isCancelledOrder = (order) => {
  const status = normalizeValue(order?.status);
  const deliveryStatus = normalizeValue(order?.delivery_partner_status);

  return (
    status.includes("cancel") ||
    status.includes("reject") ||
    status.includes("failed") ||
    deliveryStatus.includes("cancel") ||
    deliveryStatus.includes("reject") ||
    deliveryStatus.includes("failed")
  );
};

const isRefundedOrder = (order) => {
  const status = normalizeValue(order?.status);
  const paymentStatus = normalizeValue(order?.payment_status);

  return status.includes("refund") || paymentStatus.includes("refund");
};

const isPreparingOrder = (order) => {
  if (
    isDeliveredOrder(order) ||
    isCancelledOrder(order) ||
    isRefundedOrder(order)
  ) {
    return false;
  }

  const status = normalizeValue(order?.status);
  const deliveryStatus = normalizeValue(order?.delivery_partner_status);

  return (
    status.includes("paid") ||
    status.includes("pending") ||
    status.includes("preparing") ||
    status.includes("processing") ||
    status.includes("accepted") ||
    status.includes("ready") ||
    deliveryStatus.includes("unassigned") ||
    deliveryStatus.includes("assigned") ||
    deliveryStatus.includes("acknowledged") ||
    deliveryStatus.includes("picked") ||
    deliveryStatus.includes("transit")
  );
};

const getDisplayStatus = (order) => {
  if (isDeliveredOrder(order)) {
    return "Delivered";
  }

  if (isCancelledOrder(order)) {
    return "Cancelled";
  }

  if (isRefundedOrder(order)) {
    return "Refunded";
  }

  if (isPreparingOrder(order)) {
    return "Preparing";
  }

  const originalStatus = String(order?.status || "").trim();

  return originalStatus ? originalStatus.replaceAll("_", " ") : "Pending";
};

const matchesActiveFilter = (order, activeFilter) => {
  switch (activeFilter) {
    case "Preparing":
      return isPreparingOrder(order);

    case "Delivered":
      return isDeliveredOrder(order);

    case "Cancelled":
      return isCancelledOrder(order);

    case "Refunded":
      return isRefundedOrder(order);

    case "All":
    default:
      return true;
  }
};

const getStatusClass = (status) => {
  const normalizedStatus = normalizeValue(status);

  if (normalizedStatus.includes("delivered")) {
    return "bg-green-100 text-green-700";
  }

  if (normalizedStatus.includes("cancel")) {
    return "bg-red-100 text-red-700";
  }

  if (normalizedStatus.includes("refund")) {
    return "bg-purple-100 text-purple-700";
  }

  if (normalizedStatus.includes("preparing")) {
    return "bg-yellow-100 text-yellow-700";
  }

  return "bg-gray-100 text-gray-700";
};

const parseOrderDate = (dateValue) => {
  if (!dateValue) {
    return 0;
  }

  const value = String(dateValue).trim();

  const match = value.match(
    /^(\d{1,2})\/(\d{1,2})\/(\d{4})\s+(\d{1,2}):(\d{2}):(\d{2})\s*(AM|PM)?$/i,
  );

  if (!match) {
    const fallbackDate = new Date(value).getTime();

    return Number.isNaN(fallbackDate) ? 0 : fallbackDate;
  }

  const [
    ,
    dayValue,
    monthValue,
    yearValue,
    hourValue,
    minuteValue,
    secondValue,
    meridiemValue,
  ] = match;

  let hour = Number(hourValue);
  const meridiem = String(meridiemValue || "").toUpperCase();

  if (meridiem === "PM" && hour !== 12) {
    hour += 12;
  }

  if (meridiem === "AM" && hour === 12) {
    hour = 0;
  }

  return new Date(
    Number(yearValue),
    Number(monthValue) - 1,
    Number(dayValue),
    hour,
    Number(minuteValue),
    Number(secondValue),
  ).getTime();
};

export default function Orders() {
  const { user, authLoading } = useGlobalContext();
  const navigate = useNavigate();
 const location = useLocation();

  const {
    orders,
    loading,
    error,
    getUserCatalogOrders,
    getOrderReceiptDetails,
  } = useOrders();
  const { getDriverTrackingUrl } = useDriverTracker();

  const [selectedOrder, setSelectedOrder] = useState(null);
  const [showOrderModal, setShowOrderModal] = useState(false);
  const [activeFilter, setActiveFilter] = useState("All");
  const [searchText, setSearchText] = useState("");
  const [downloadingInvoiceId, setDownloadingInvoiceId] = useState("");

  const selectedOrderIdFromDashboard = String(
    location.state?.selectedOrderId || "",
  ).trim();

  const dashboardAction = String(location.state?.action || "")
    .trim()
    .toLowerCase();

  useEffect(() => {
    if (authLoading) {
      return;
    }

    const customerMobile = formatCustomerMobile(user?.phone || user?.number);

    if (!customerMobile) {
      return;
    }

    getUserCatalogOrders({
      custMobile: customerMobile,
      pageIndex: "1",
      recordPerPage: "10",
    });
  }, [authLoading, getUserCatalogOrders, user?.number, user?.phone]);

  const filteredOrders = useMemo(() => {
    const searchValue = searchText.trim().toLowerCase();

    return [...orders]
      .filter((order) => {
        return matchesActiveFilter(order, activeFilter);
      })
      .filter((order) => {
        if (!searchValue) {
          return true;
        }

        const searchableValues = [
          order?.organization_name,
          order?.restaurant_name,
          order?.orderid,
          order?.customer_name,
          order?.customer_mobile,
          order?.orderType,
          order?.payment_mode,
          order?.payment_status,
          order?.status,
          order?.delivery_partner_status,
          getDisplayStatus(order),

          ...(Array.isArray(order?.items)
            ? order.items.map((item) => {
                return (
                  item?.product_name || item?.item_name || item?.product_id
                );
              })
            : []),
        ];

        return searchableValues.filter(Boolean).some((value) => {
          return String(value).toLowerCase().includes(searchValue);
        });
      })
      .sort((firstOrder, secondOrder) => {
        return (
          parseOrderDate(secondOrder?.order_date) -
          parseOrderDate(firstOrder?.order_date)
        );
      });
  }, [activeFilter, orders, searchText]);

  const openOrderDetails = (order) => {
    setSelectedOrder(order);
    setShowOrderModal(true);
  };

  const closeOrderDetails = () => {
    setShowOrderModal(false);
    setSelectedOrder(null);
  };

const handleTrackOrder = useCallback(
  async (order) => {
    const orderId = String(order?.orderid || "").trim();

    if (!orderId) {
      console.error("Order ID is missing");
      return;
    }

    // Open the new tab immediately from the user's click
    const trackingTab = window.open("about:blank", "_blank");

    if (!trackingTab) {
      console.error("Popup was blocked by the browser.");
      return;
    }

    try {
      const trackingUrl = await getDriverTrackingUrl({
        orderid: orderId,
      });

      // Navigate the already-opened tab to API returned URL
      trackingTab.location.href = trackingUrl;
    } catch (error) {
      console.error("Unable to track order:", error);

      // Close blank tab if API fails
      trackingTab.close();
    }
  },
  [getDriverTrackingUrl],
);

const handleInvoiceDownload = useCallback(
  async (order) => {
    const orderId = String(
      order?.orderid || order?.orderId || order?.order_id || "",
    ).trim();

    if (!orderId) {
      console.error("Invoice order ID missing:", order);
      return;
    }

    if (downloadingInvoiceId) {
      return;
    }

    // =====================================================
    // NEW TAB USER CLICK KE TIME HI OPEN KARO
    // Taaki browser popup block na kare
    // =====================================================

    const invoiceTab = window.open("about:blank", "_blank");

    if (!invoiceTab) {
      console.error("Invoice tab was blocked by the browser.");
      return;
    }

    try {
      setDownloadingInvoiceId(orderId);

      // =====================================================
      // RECEIPT API
      // =====================================================

      const response = await getOrderReceiptDetails({
        orderid: orderId,
        platform: "WEB",
      });

      console.log("DOWNLOAD RECEIPT RESPONSE:", response);

      const receiptResponse =
        response?.response ??
        response?.data?.response ??
        response?.data ??
        null;

      const receipt = Array.isArray(receiptResponse)
        ? receiptResponse[0] || null
        : receiptResponse;

      if (!receipt) {
        throw new Error(response?.responseMessage || "Invoice data not found.");
      }

      // =====================================================
      // RECEIPT TEMPORARILY STORE
      // New tab React Router state receive nahi karega
      // =====================================================

      localStorage.setItem(
        `invoice-receipt-${orderId}`,
        JSON.stringify(receipt),
      );

      // =====================================================
      // SAME PAGE NAHI — NEW TAB ME OPEN
      // =====================================================

      invoiceTab.location.href = `/order-summary?orderid=${encodeURIComponent(
        orderId,
      )}&download=1`;
    } catch (error) {
      console.error("Invoice download error:", error);

      invoiceTab.close();
    } finally {
      setDownloadingInvoiceId("");
    }
  },
  [downloadingInvoiceId, getOrderReceiptDetails],
);

  const handleDashboardOrderAction = useCallback(() => {
    if (
      !selectedOrderIdFromDashboard ||
      !dashboardAction ||
      !Array.isArray(orders) ||
      orders.length === 0
    ) {
      return;
    }

    const matchedOrder = orders.find((order) => {
      const currentOrderId = String(
        order?.orderid || order?.orderId || order?.order_id || order?.id || "",
      ).trim();

      return currentOrderId === selectedOrderIdFromDashboard;
    });

    if (!matchedOrder) {
      console.error(
        "Selected dashboard order not found:",
        selectedOrderIdFromDashboard,
      );

      return;
    }

    if (dashboardAction === "view") {
      setSelectedOrder(matchedOrder);
      setShowOrderModal(true);
    }

    if (dashboardAction === "invoice") {
      handleInvoiceDownload(matchedOrder);
    }

    if (dashboardAction === "track") {
      handleTrackOrder(matchedOrder);
    }

    if (dashboardAction === "reorder") {
      handleReorder(matchedOrder);
    }

    // Action dobara execute na ho jab component re-render ho
    navigate(location.pathname, {
      replace: true,
      state: {},
    });
  }, [
    dashboardAction,
    location.pathname,
    navigate,
    orders,
    selectedOrderIdFromDashboard,
    handleTrackOrder,
  ]);

const handleReorder = useCallback(
  (order) => {
    const restaurantId = String(
      order?.fk_user_id || order?.user_id || order?.restaurant_id || "",
    ).trim();

    if (!restaurantId) {
      console.error("Restaurant ID is missing for reorder:", order);
      return;
    }

    navigate(`/restaurant-details/${restaurantId}`, {
      state: {
        reorderOrder: order,
      },
    });
  },
  [navigate],
);

  useEffect(() => {
    handleDashboardOrderAction();
  }, [handleDashboardOrderAction]);

  return (
    <div className="rounded-3xl border border-gray-100 bg-white p-4 shadow-sm sm:p-6">
      {/* Heading */}

      <h2 className="mb-5 text-2xl font-semibold text-gray-800 sm:text-[28px]">
        Recent Orders
      </h2>

      {/* Search */}

      <div className="relative mb-5">
        <Search
          size={18}
          className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400"
        />

        <input
          type="text"
          value={searchText}
          onChange={(event) => {
            setSearchText(event.target.value);
          }}
          placeholder="Search restaurant, order ID or item..."
          className="h-12 w-full rounded-full border border-gray-300 pl-12 pr-4 text-sm outline-none focus:ring-2 focus:ring-[#1F1A56]/20"
        />
      </div>

      {/* Filters */}
      <div
        className="
    mb-6
    flex
    w-full
    gap-3
    overflow-x-auto
    overflow-y-hidden
    pb-2
    [scrollbar-width:none]
    [-ms-overflow-style:none]
    [&::-webkit-scrollbar]:hidden
  "
      >
        {filters.map((filter) => (
          <button
            type="button"
            key={filter}
            onClick={() => {
              setActiveFilter(filter);
            }}
            className={`shrink-0 whitespace-nowrap rounded-full px-4 py-2 text-sm transition ${
              activeFilter === filter
                ? "bg-[#1F1A56] text-white"
                : "border bg-white text-gray-600 hover:bg-gray-100"
            }`}
          >
            {filter}
          </button>
        ))}
      </div>

      {/* Loading */}

      {loading ? (
        <div className="flex min-h-[220px] items-center justify-center">
          <div className="h-9 w-9 animate-spin rounded-full border-4 border-gray-200 border-t-[#1F1A56]" />
        </div>
      ) : filteredOrders.length === 0 ? (
        /* Empty State */

        <div className="rounded-2xl border border-dashed border-gray-300 px-4 py-14 text-center">
          <p className="font-medium text-gray-700">No orders found</p>

          <p className="mt-1 text-sm text-gray-400">
            {error || `No ${activeFilter.toLowerCase()} orders available.`}
          </p>
        </div>
      ) : (
        /* Orders List */

        <div
          className="
    grid
    grid-cols-1
    gap-4

    md:grid-cols-2
    md:gap-5
  "
        >
          {filteredOrders.map((order, index) => {
            const displayStatus = getDisplayStatus(order);

            const orderKey = order?.orderid || order?.id || index;

            const itemCount = Array.isArray(order?.items)
              ? order.items.length
              : Number(
                  order?.item_count ||
                    order?.itemCount ||
                    order?.total_items ||
                    0,
                );

            const restaurantName =
              order?.organization_name ||
              order?.restaurant_name ||
              order?.shop_name ||
              "Restaurant";

            const orderId =
              order?.orderid ||
              order?.orderId ||
              order?.order_id ||
              order?.id ||
              "-";

            const amount =
              order?.pay_amount ||
              order?.total_amount ||
              order?.grand_total ||
              order?.total_order_price ||
              order?.amount ||
              0;

            const currentInvoiceId = String(
              order?.orderid || order?.orderId || order?.order_id || "",
            );

            const isDownloading = downloadingInvoiceId === currentInvoiceId;

            const normalizedStatus = String(displayStatus || "")
              .trim()
              .replaceAll("_", " ");

            const statusKey = normalizedStatus.toLowerCase();

            const isTrackable =
              statusKey.includes("on the way") ||
              statusKey.includes("out for delivery") ||
              statusKey.includes("picked") ||
              statusKey.includes("dispatch") ||
              normalizeValue(order?.delivery_partner_status).includes(
                "picked",
              ) ||
              normalizeValue(order?.delivery_partner_status).includes(
                "transit",
              );

            const isPreparing =
              statusKey.includes("preparing") ||
              statusKey.includes("processing") ||
              statusKey.includes("accepted") ||
              statusKey.includes("received");

            const isCompleted =
              statusKey.includes("delivered") ||
              statusKey.includes("completed");

            const isReadyForTakeaway =
              statusKey.includes("ready") &&
              normalizeValue(order?.orderType) === "takeaway";

            const orderType = normalizeValue(
              order?.orderType || order?.order_type || order?.orderTypeName,
            );

            const orderTypeText =
              orderType === "takeaway"
                ? "Takeaway order"
                : orderType === "dining" ||
                    orderType === "dinein" ||
                    orderType === "dine-in"
                  ? "Dining order"
                  : orderType === "delivery"
                    ? "Delivery order"
                    : orderType
                      ? `${
                          orderType.charAt(0).toUpperCase() + orderType.slice(1)
                        } order`
                      : "Order";

            return (
              <article
                key={orderKey}
                className="
        overflow-hidden
        rounded-[15px]
        border
        border-[#dce3eb]
        bg-white
        shadow-[0_2px_8px_rgba(15,23,42,0.025)]
        transition
        hover:border-[#ccd6e2]
        hover:shadow-[0_6px_16px_rgba(15,23,42,0.05)]
      "
              >
                {/* TOP */}
                <div className="flex items-start justify-between gap-3 px-4 py-4 sm:px-5">
                  <div className="flex min-w-0 items-center gap-3">
                    {/* RESTAURANT IMAGE */}
                    <div
                      className="
    h-[44px]
    w-[44px]
    shrink-0
    overflow-hidden
    rounded-[11px]
    border
    border-[#e5e7eb]
    bg-[#f8fafc]
  "
                    >
                      <img
                        src={getOrderImage(order)}
                        alt={restaurantName}
                        loading="lazy"
                        onError={(event) => {
                          event.currentTarget.onerror = null;
                          event.currentTarget.src = NoCafe;
                        }}
                        className="
      h-full
      w-full
      object-cover
    "
                      />
                    </div>

                    {/* NAME + DATE */}
                    <div className="min-w-0">
                      <h4
                        title={restaurantName}
                        className="
                truncate
                text-[15px]
                font-bold
                leading-5
                text-[#111827]
                sm:text-[16px]
              "
                      >
                        {restaurantName}
                      </h4>

                      <div className="mt-1 flex min-w-0 items-center gap-1.5 text-[#8794a8]">
                        <Clock
                          size={13}
                          strokeWidth={1.8}
                          className="shrink-0"
                        />

                        <span className="truncate text-[10px] sm:text-[11px]">
                          {formatOrderDate(
                            order?.order_date ||
                              order?.created_on ||
                              order?.created_at,
                          )}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* STATUS */}
                  <span
                    className={`
            inline-flex
            max-w-[145px]
            shrink-0
            items-center
            gap-1.5
            rounded-full
            px-2.5
            py-1.5
            text-[9px]
            font-semibold
            capitalize
            sm:px-3
            sm:text-[10px]

            ${getStatusClass(displayStatus)}
          `}
                  >
                    <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-current" />

                    <span className="truncate">{normalizedStatus}</span>
                  </span>
                </div>

                {/* ORDER ID */}
                <div
                  className="
          flex
          items-center
          justify-between
          gap-3
          border-t
          border-[#e7ebf0]
          bg-[#fbfcfe]
          px-4
          py-3
          sm:px-5
        "
                >
                  <span
                    className="
            text-[10px]
            font-medium
            uppercase
            tracking-[0.04em]
            text-[#76859a]
            sm:text-[11px]
          "
                  >
                    Order ID
                  </span>

                  <span
                    title={String(orderId)}
                    className="
            min-w-0
            truncate
            text-right
            text-[11px]
            font-bold
            text-[#172033]
            sm:text-[12px]
          "
                  >
                    #{orderId}
                  </span>
                </div>

                {/* ITEM COUNT + AMOUNT */}
                <div className="grid grid-cols-2 border-[#e7ebf0]">
                  <div
                    className="
            flex
            items-center
            px-4
            py-4
            sm:px-5
          "
                  >
                    <span className="text-[13px] font-bold text-[#111827] sm:text-[14px]">
                      {itemCount} {itemCount === 1 ? "Item" : "Items"}
                    </span>
                  </div>

                  <div
                    className="
            flex
            items-center
            justify-end
            px-4
            py-4
            sm:px-5
          "
                  >
                    <span className="text-[13px] font-bold text-[#111827] sm:text-[14px]">
                      ₹ {formatAmount(amount)}
                    </span>
                  </div>
                </div>

                {/* BOTTOM ACTION BAR */}
                <div
                  className="
          flex
          min-h-[56px]
          items-center
          justify-between
          gap-3
          border-t
          border-[#e7ebf0]
          px-4
          py-2
          sm:px-5
        "
                >
                  {/* LEFT ACTIONS */}
                  <div className="flex items-center gap-1">
                    {/* VIEW */}
                    <button
                      type="button"
                      title="View Details"
                      aria-label="View Details"
                      onClick={() => openOrderDetails(order)}
                      className="
              flex
              h-9
              w-9
              items-center
              justify-center
              rounded-full
              text-[#53657c]
              transition
              hover:bg-blue-50
              hover:text-[#2454e6]
              active:scale-95
            "
                    >
                      <Eye size={17} strokeWidth={1.8} />
                    </button>

                    {/* DOWNLOAD */}
                    <button
                      type="button"
                      title="Download Invoice"
                      aria-label="Download Invoice"
                      disabled={Boolean(downloadingInvoiceId)}
                      onClick={() => handleInvoiceDownload(order)}
                      className="
              flex
              h-9
              w-9
              items-center
              justify-center
              rounded-full
              text-[#53657c]
              transition
              hover:bg-blue-50
              hover:text-[#2454e6]
              disabled:cursor-not-allowed
              disabled:opacity-50
              active:scale-95
            "
                    >
                      {isDownloading ? (
                        <Loader2 size={16} className="animate-spin" />
                      ) : (
                        <Download size={16} strokeWidth={1.8} />
                      )}
                    </button>
                  </div>

                  {/* RIGHT ACTION */}
                  <div className="ml-auto flex shrink-0 items-center justify-end">
                    {isDeliveredOrder(order) ? (
                      <button
                        type="button"
                        onClick={() => handleReorder(order)}
                        className="
        inline-flex
        h-9
        items-center
        justify-center
        gap-1.5
        rounded-[9px]
        bg-[#315bea]
        px-3.5
        text-[10px]
        font-semibold
        text-white
        shadow-[0_4px_10px_rgba(49,91,234,0.18)]
        transition
        hover:bg-[#244bd0]
        active:scale-[0.98]
        sm:px-4
        sm:text-[11px]
      "
                      >
                        <RotateCcw size={14} strokeWidth={2} />
                        Reorder
                      </button>
                    ) : (
                      <button
                        type="button"
                        onClick={() => handleTrackOrder(order)}
                        className="
        inline-flex
        h-9
        items-center
        justify-center
        gap-1.5
        rounded-[9px]
        bg-[#315bea]
        px-3.5
        text-[10px]
        font-semibold
        text-white
        shadow-[0_4px_10px_rgba(49,91,234,0.18)]
        transition
        hover:bg-[#244bd0]
        active:scale-[0.98]
        sm:px-4
        sm:text-[11px]
      "
                      >
                        <MapPin size={14} strokeWidth={2} />
                        Track Order
                      </button>
                    )}
                  </div>
                </div>
              </article>
            );
          })}
        </div>
      )}

      {/* Order Details Modal */}

      <OrderDetailsModal
        open={showOrderModal}
        order={selectedOrder}
        onClose={closeOrderDetails}
        onDownload={handleInvoiceDownload}
        onReorder={handleReorder}
      />
    </div>
  );
}
